import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import YouTubePlaylistItemWithDuration from '../../models/gapi/gapiPlaylistItemExtensions';
import Playlist from '../../models/playlist/playlist';
import SyncSource from '../../models/playlist/syncSource';
import {
  localYouTubeIds,
  replaceSource,
  sourceDirection,
  visibleIds,
} from '../../models/playlist/syncSourceHelpers';
import SyncType from '../../models/playlist/syncType';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import Source from '../../models/source';
import YouTubeTrack from '../../models/track/youTubeTrack';
import SyncProvider from '../../models/synchronization/syncProvider';
import { Keys, messages } from '../../settings';
import { GoogleScope, IGoogleApiContext } from '../gapi/gapiService';
import handleGapiError from '../gapi/handleGapiError';
import { ILibraryContext } from '../library/libraryService';
import { GlobalState } from '../_globalContext/context';
import { diffYouTubeSync } from './youTubeSyncDiff';
import { classifyWriteFailure, WriteFailure } from './youTubeWriteErrors';
import { showLibraryErrors } from '../library/libraryError';

interface RemoteItem {
  videoId: string;
  /** The playlist item id - what playlistItems.delete takes. */
  playlistItemId: string;
  item: YouTubePlaylistItemWithDuration;
}

export interface YouTubeSyncDeps {
  /**
   * Getters rather than values: the provider is queued and may run long after
   * it was created, and it must act on the playlist as it is *now*, not as it
   * was when the sync was requested. See the comment on SyncProvider.
   */
  gapi(): IGoogleApiContext;
  lib(): ILibraryContext;
  ctx(): GlobalState;
}

async function fetchRemote(gapiCtx: IGoogleApiContext, playlistId: string) {
  const remote: RemoteItem[] = [];

  let page = await gapiCtx.getPlaylistItemsPaged(playlistId);

  for (;;) {
    for (const item of page.results) {
      const videoId = item.contentDetails?.videoId;

      // Every item has both; the guard is for the typings.
      if (videoId && item.id) {
        remote.push({ videoId, playlistItemId: item.id, item });
      }
    }

    if (!page.next) {
      break;
    }

    page = await page.next();
  }

  return remote;
}

/** The video's title from the library, for a message a person can act on. */
function describeVideo(deps: YouTubeSyncDeps, videoId: string) {
  const track = deps.lib().getLibraryTrack(new PlaylistItem(videoId, Source.YouTube));

  return track && track.type !== Source.Null && track.title
    ? `${track.title} (${videoId})`
    : videoId;
}

/**
 * Says what actually failed, once per sync rather than once per video.
 *
 * The first version of this reported only a count, at error level, through
 * callSnackbar - which meant Sentry received "2 changes could not be written
 * to YouTube" and nothing else: no video, no reason, no way to tell a dead
 * video from an exhausted quota. The detail went to console.warn, which
 * survives the production logging strip but only ever reaches a breadcrumb.
 *
 * So the report is built here instead, and the severity follows the kind of
 * failure. Permanent ones are a fact about the user's data, now recorded and
 * not repeating. Transient ones are a warning that will resolve itself.
 */
function reportPushFailures(
  deps: YouTubeSyncDeps,
  playlist: Playlist,
  source: SyncSource,
  failures: WriteFailure[],
) {
  const permanent = failures.filter((x) => x.permanent);
  const transient = failures.filter((x) => !x.permanent);

  const lines: string[] = [];

  if (permanent.length) {
    const named = permanent.slice(0, 3).map((x) => describeVideo(deps, x.videoId));
    const rest = permanent.length - named.length;

    lines.push(
      `${permanent.length} video${permanent.length === 1 ? '' : 's'} could not be synced to YouTube and will be skipped from now on: ` +
        named.join(', ') +
        (rest > 0 ? `, and ${rest} more` : '') +
        '. Use Synchronize on the playlist to try them again.',
    );
  }

  if (transient.length) {
    lines.push(
      `${transient.length} change${transient.length === 1 ? '' : 's'} could not be written to YouTube (${transient[0].reason}). They will be retried on the next sync.`,
    );
  }

  const message = lines.join(' ');

  // Not an error report: callSnackbar would send this to Sentry with the
  // message and nothing else. Shown to the user here, reported below with
  // the detail attached.
  deps.ctx().callSnackbar(message, false);

  Sentry.withScope((scope) => {
    scope.setExtra('playlist', playlist.name);
    scope.setExtra('youTubePlaylistId', source.sourceId);
    scope.setExtra(
      'failures',
      failures.map((x) => ({
        videoId: x.videoId,
        operation: x.operation,
        reason: x.reason,
        permanent: x.permanent,
      })),
    );

    Sentry.captureMessage(
      `YouTube sync: ${permanent.length} permanent, ${transient.length} transient write failures`,
      Severity.Warning,
    );
  });
}

/**
 * Keeps a Fadila playlist and a YouTube playlist in step.
 *
 * Looks only at YouTube content: tracks from other sources in the Fadila
 * playlist are never touched, and nothing about ordering is synced. Which
 * changes travel which way is the source's direction; how the changes are
 * worked out from the two current states and the last snapshot is
 * youTubeSyncDiff.ts.
 *
 * Writes to YouTube need the youtube (manage) scope. Without it the sync
 * still pulls, and whatever should have been pushed stays pending: the
 * snapshot only ever records what YouTube actually holds, so a local change
 * that could not be pushed is still a difference next time round.
 */
export default function createYouTubeSyncProvider(deps: YouTubeSyncDeps): SyncProvider {
  return async function youTubeSync(source: SyncSource, playlist: Playlist) {
    const gapiCtx = deps.gapi();
    const ctx = deps.ctx();

    if (source.type !== SyncType.YouTubePlaylist) {
      return;
    }

    // Not an error: the user simply has not signed in to YouTube in this
    // session. Selecting the playlist again after signing in syncs it.
    if (
      !gapiCtx.gapiClientLoaded ||
      !gapiCtx.gapiAuthorized ||
      gapiCtx.authorizedScopes.indexOf(GoogleScope.YouTube) === -1
    ) {
      console.debug('YouTube sync skipped, not signed in to YouTube', playlist.name);
      return;
    }

    const errorHandler = handleGapiError(ctx.callSnackbar, gapiCtx.authorizationExpired);

    try {
      const remote = await fetchRemote(gapiCtx, source.sourceId);

      // Everything below reads the playlist afresh: the fetch took time and
      // the user may have edited the playlist, or unlinked it, meanwhile.
      const current = deps.lib().playlists.find((pl) => pl.id === playlist.id);
      const currentSource = current?.syncSources.find(
        (x) => x.type === source.type && x.sourceId === source.sourceId,
      );

      if (!current || !currentSource) {
        console.debug('YouTube sync abandoned, playlist or source is gone', playlist.name);
        return;
      }

      const direction = sourceDirection(currentSource);
      const canPush = gapiCtx.canWriteYouTube;

      // Ids this source has given up on are dropped from every side of the
      // comparison, so they read as absent rather than as a change. See
      // visibleIds for why all three and not just the local one.
      const diff = diffYouTubeSync({
        base: currentSource.syncedIds && visibleIds(currentSource, currentSource.syncedIds),
        remote: visibleIds(
          currentSource,
          remote.map((x) => x.videoId),
        ),
        local: visibleIds(currentSource, localYouTubeIds(current)),
        direction,
        canPush,
      });

      console.debug('YouTube sync', playlist.name, direction, diff);

      // --- Push: Fadila -> YouTube -------------------------------------
      //
      // The snapshot is advanced to what YouTube holds after this, so a write
      // that fails leaves its id out (or in) and is attempted again next
      // time. That is right for a quota limit and wrong for a deleted video,
      // which would be retried forever - so classifyWriteFailure separates
      // the two and the hopeless ones go to skippedIds instead.
      //
      // finalRemote is built from the *unfiltered* remote, so the snapshot
      // records what YouTube actually holds, skipped ids included. That keeps
      // it truthful for the day the user clears them.
      const finalRemote = new Set(remote.map((x) => x.videoId));
      const failures: WriteFailure[] = [];

      for (const videoId of diff.pushAdds) {
        try {
          await gapiCtx.insertPlaylistItem(source.sourceId, videoId);
          finalRemote.add(videoId);
        } catch (err) {
          const { reason, permanent } = classifyWriteFailure(err, 'add');

          failures.push({ videoId, operation: 'add', reason, permanent, alreadyGone: false });
          console.warn('YouTube sync: failed to add video', videoId, reason, err);
        }
      }

      for (const videoId of diff.pushRemoves) {
        // A video listed twice on YouTube has two playlist items. Remove all.
        let removedAll = true;

        for (const entry of remote.filter((x) => x.videoId === videoId)) {
          try {
            await gapiCtx.deletePlaylistItem(entry.playlistItemId);
          } catch (err) {
            const { reason, permanent, alreadyGone } = classifyWriteFailure(err, 'remove');

            // Nothing there to remove is the outcome we wanted, not a failure.
            if (alreadyGone) {
              continue;
            }

            removedAll = false;
            failures.push({ videoId, operation: 'remove', reason, permanent, alreadyGone });
            console.warn('YouTube sync: failed to remove video', videoId, reason, err);
          }
        }

        if (removedAll) {
          finalRemote.delete(videoId);
        }
      }

      const newlySkipped = failures.filter((x) => x.permanent).map((x) => x.videoId);

      // --- Pull: YouTube -> Fadila -------------------------------------
      //
      // Read the playlist once more; the pushes above were more network time.
      const latest = deps.lib().playlists.find((pl) => pl.id === playlist.id) || current;
      let tracks = latest.tracks;

      if (diff.pullRemoves.length) {
        const gone = new Set(diff.pullRemoves);

        tracks = tracks.filter((x) => !(x.trackType === Source.YouTube && gone.has(x.id)));
      }

      if (diff.pullAdds.length) {
        const wanted = new Set(diff.pullAdds);
        const newTracks: YouTubeTrack[] = [];

        for (const entry of remote) {
          if (wanted.has(entry.videoId)) {
            // Once per video, however many times YouTube lists it.
            wanted.delete(entry.videoId);
            newTracks.push(YouTubeTrack.from(entry.item));
          }
        }

        // addToLibrary is a no-op for tracks already in the library, and the
        // playlist gets a reference either way.
        const added = showLibraryErrors(ctx.callSnackbar, () =>
          deps.lib().addToLibrary(...newTracks),
        );

        if (added) {
          tracks = tracks.concat(newTracks.map(PlaylistItem.from));
        }
      }

      const updated = replaceSource(
        { ...latest, tracks },
        {
          ...currentSource,
          lastUpdated: Date.now(),
          syncedIds: Array.from(finalRemote),
          skippedIds: newlySkipped.length
            ? Array.from(new Set((currentSource.skippedIds || []).concat(newlySkipped)))
            : currentSource.skippedIds,
        },
      );

      // Always saved, even when nothing changed: the snapshot and timestamp did.
      deps.lib().updatePlaylist(updated);

      if (failures.length) {
        reportPushFailures(deps, latest, currentSource, failures);
      }
    } catch (err) {
      errorHandler(err, messages(Keys.youTubeSyncFailed));
    }
  };
}
